/**
 * FillableForm - Displays a specific fillable PDF form
 * 
 * Fetches the form from the backend based on menu item ID
 * Opens the PDF in an iframe for viewing/filling
 * Provides print and download actions
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';

// Fallback: Map menu item IDs to PDF filenames (for manually uploaded files)
const FALLBACK_FORM_FILES = {
  'qa-qc-fillable-concrete-inspection': 'Concrete Inspection- Fillable.pdf',
  'qa-qc-fillable-conveyors-feeders': 'Conveyors and Feeders- Fillable.pdf',
  'qa-qc-fillable-exhibit-b-incoming-inspection': 'Exhibit B Incoming Inspection- Fillable.pdf',
  'qa-qc-fillable-grout-inspection': 'Grout Inspection- Fillable.pdf',
  'qa-qc-fillable-heat-exchangers': 'Heat Exchangers- Fillable.pdf',
  'qa-qc-fillable-pipe-inspection-master': 'Pipe Inspection Form - Master- Fillable.pdf',
  'qa-qc-fillable-pumps': 'Pumps- Fillable.pdf',
  'qa-qc-fillable-screw-conveyors-feeders': 'Screw Conveyors and Feeders- Fillable.pdf',
  'qa-qc-fillable-structural-steel-completion': 'Structural Steel Completion Checklist- Fillable.pdf',
  'qa-qc-fillable-structural-steel-inspection': 'Structural Steel Inspection Checklist- Fillable.pdf',
  'qa-qc-fillable-tanks': 'Tanks- Fillable.pdf',
};

// Map menu item IDs to display names
const FORM_NAMES = {
  'qa-qc-fillable-concrete-inspection': 'Concrete Inspection',
  'qa-qc-fillable-conveyors-feeders': 'Conveyors and Feeders',
  'qa-qc-fillable-exhibit-b-incoming-inspection': 'Exhibit B Incoming Inspection',
  'qa-qc-fillable-grout-inspection': 'Grout Inspection',
  'qa-qc-fillable-heat-exchangers': 'Heat Exchangers',
  'qa-qc-fillable-pipe-inspection-master': 'Pipe Inspection Form - Master',
  'qa-qc-fillable-pumps': 'Pumps',
  'qa-qc-fillable-screw-conveyors-feeders': 'Screw Conveyors and Feeders',
  'qa-qc-fillable-structural-steel-completion': 'Structural Steel Completion Checklist',
  'qa-qc-fillable-structural-steel-inspection': 'Structural Steel Inspection Checklist',
  'qa-qc-fillable-tanks': 'Tanks',
};

const FillableForm = ({ formId, projectDetails }) => {
  const { instance, accounts } = useMsal();
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState(null);

  const formName = FORM_NAMES[formId] || 'Form';

  // Colors
  const colors = {
    primary: '#6366f1',
    dark: '#1e293b',
    light: '#f1f5f9',
    border: '#e2e8f0',
    success: '#10b981',
  };

  // Fetch form data from backend
  const fetchFormData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Try to get the form from the backend metadata
      const response = await authenticatedApiCall(instance, accounts, 'GET', `/forms/by-menu/${formId}`);
      const forms = response?.data?.forms || [];
      
      if (forms.length > 0) {
        // Use the first form found for this menu item
        setFormData({
          filename: forms[0].filename,
          displayName: forms[0].displayName,
          pdfUrl: `/pdfs/qaqc/${forms[0].filename}`,
        });
      } else {
        // Fallback to hardcoded filename
        const fallbackFilename = FALLBACK_FORM_FILES[formId];
        if (fallbackFilename) {
          setFormData({
            filename: fallbackFilename,
            displayName: formName,
            pdfUrl: `/pdfs/qaqc/${fallbackFilename}`,
          });
        } else {
          setError('Form not found');
        }
      }
    } catch (err) {
      // If API fails, try fallback
      const fallbackFilename = FALLBACK_FORM_FILES[formId];
      if (fallbackFilename) {
        setFormData({
          filename: fallbackFilename,
          displayName: formName,
          pdfUrl: `/pdfs/qaqc/${fallbackFilename}`,
        });
      } else {
        setError('Failed to load form');
      }
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, formId, formName]);

  useEffect(() => {
    fetchFormData();
  }, [fetchFormData]);

  const handlePrint = () => {
    if (formData?.pdfUrl) {
      const printWindow = window.open(formData.pdfUrl, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
    }
  };

  const handleDownload = () => {
    if (formData?.pdfUrl) {
      const link = document.createElement('a');
      link.href = formData.pdfUrl;
      link.download = formData.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleOpenInNewTab = () => {
    if (formData?.pdfUrl) {
      window.open(formData.pdfUrl, '_blank');
    }
  };

  // Loading state
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '60vh',
        color: '#64748b'
      }}>
        <div style={{ fontSize: '2rem', marginBottom: '1rem', animation: 'spin 1s linear infinite' }}>📄</div>
        <p>Loading form...</p>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Error state
  if (error || !formData) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '60vh',
        color: '#64748b'
      }}>
        <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📄</div>
        <h3 style={{ margin: 0, color: colors.dark }}>Form Not Available</h3>
        <p style={{ textAlign: 'center', maxWidth: '400px', marginTop: '0.5rem' }}>
          The form "{formName}" has not been uploaded yet.
          <br /><br />
          <span style={{ fontSize: '0.85rem', color: '#94a3b8' }}>
            Admins can upload this form via Technical → Forms → Upload Forms
          </span>
        </p>
      </div>
    );
  }

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: 'calc(100vh - 200px)',
      backgroundColor: colors.light,
      borderRadius: '8px',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '1rem 1.5rem',
        backgroundColor: 'white',
        borderBottom: `1px solid ${colors.border}`,
        flexWrap: 'wrap',
        gap: '0.5rem',
      }}>
        <div>
          <h3 style={{ margin: 0, color: colors.dark, fontSize: '1.1rem' }}>
            📝 {formData.displayName || formName}
          </h3>
          <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.85rem' }}>
            Fillable PDF Form • Fill in the fields and print or download
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button
            onClick={handleOpenInNewTab}
            style={{
              padding: '8px 16px',
              backgroundColor: 'white',
              color: colors.dark,
              border: `1px solid ${colors.border}`,
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.85rem',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            🔗 Open in New Tab
          </button>
          <button
            onClick={handleDownload}
            style={{
              padding: '8px 16px',
              backgroundColor: 'white',
              color: colors.dark,
              border: `1px solid ${colors.border}`,
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.85rem',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            ⬇️ Download
          </button>
          <button
            onClick={handlePrint}
            style={{
              padding: '8px 16px',
              backgroundColor: colors.primary,
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.85rem',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            🖨️ Print
          </button>
        </div>
      </div>

      {/* PDF Viewer */}
      <div style={{ flex: 1, position: 'relative' }}>
        {pdfLoading && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: '#64748b',
            zIndex: 1,
          }}>
            Loading PDF...
          </div>
        )}
        <iframe
          src={formData.pdfUrl}
          title={formData.displayName || formName}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
          }}
          onLoad={() => setPdfLoading(false)}
          onError={() => {
            setPdfLoading(false);
            setError('Failed to load PDF');
          }}
        />
      </div>

      {/* Footer tip */}
      <div style={{
        padding: '0.75rem 1.5rem',
        backgroundColor: '#fef3c7',
        borderTop: `1px solid #f59e0b`,
        fontSize: '0.8rem',
        color: '#92400e',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
      }}>
        <span>💡</span>
        <span>
          <strong>Tip:</strong> Fill in the form fields directly in the PDF viewer above, then use Print or Download to save your completed form.
        </span>
      </div>
    </div>
  );
};

export default FillableForm;
